// apps/frontend/src/components/common/CommunityVoiceModal.tsx
// Modal Community Voice — "Marie-Amelie a une question pour toi".
// Multi-step (1 question par ecran), Glowp brand intime (avatar
// fondateur, cream/beige, ecriture chaleureuse). Jamais le mot
// "sondage" cote user. Skip-per-question + dismiss X global.

import React, { useState } from 'react';
import {
  X,
  ChevronRight,
  Send,
  Heart,
  Sparkles,
  Flame,
  Cloud,
  Compass,
  HelpCircle,
  Star,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  useNextQuestionForMe,
  type PendingQuestion,
} from '../../hooks/useNextQuestionForMe';
import { COMMUNITY_VOICE_COPY as COPY } from '../../lib/communityVoiceCopy';

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  sparkles: Sparkles,
  flame: Flame,
  compass: Compass,
  cloud: Cloud,
  help: HelpCircle,
  heart: Heart,
  star: Star,
};

function renderIcon(name: string | undefined, className: string) {
  if (!name) return null;
  const Comp = ICON_MAP[name.toLowerCase()];
  if (!Comp) return null;
  return <Comp className={className} />;
}

interface AnswerState {
  text?: string;
  rating?: number;
  choice?: string | string[];
}

// Garde-fou : ne pas demander de feedback a quelqu un qui vient
// tout juste d arriver. 24h minimum apres signup pour laisser le user
// experimenter Glowp avant qu on lui pose la moindre question.
const ACCOUNT_AGE_MIN_MS = 24 * 60 * 60 * 1000;

const CommunityVoiceModal: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const accountOldEnough = (() => {
    if (!user?.createdAt) return false;
    const age = Date.now() - new Date(user.createdAt).getTime();
    return age >= ACCOUNT_AGE_MIN_MS;
  })();
  const { survey, visible, submitAnswers, dismiss } = useNextQuestionForMe(
    isAuthenticated && accountOldEnough
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [sending, setSending] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);

  if (!isAuthenticated || !visible || !survey) return null;

  const totalSteps = survey.questions.length;
  const question = survey.questions[stepIndex];
  const isLast = stepIndex >= totalSteps - 1;
  const current = answers[question.id] || {};

  const hasAnswer = (() => {
    if (current.text && current.text.trim()) return true;
    if (typeof current.rating === 'number') return true;
    if (Array.isArray(current.choice) && current.choice.length > 0) return true;
    if (typeof current.choice === 'string' && current.choice) return true;
    return false;
  })();

  const setAnswerPart = (part: AnswerState) =>
    setAnswers(prev => ({
      ...prev,
      [question.id]: { ...prev[question.id], ...part },
    }));

  const handleNext = async () => {
    if (!isLast) {
      setStepIndex(stepIndex + 1);
      return;
    }
    // Last step — submit
    setSending(true);
    try {
      const payload = Object.entries(answers)
        .filter(
          ([, a]) =>
            (a.text && a.text.trim()) ||
            typeof a.rating === 'number' ||
            (Array.isArray(a.choice) && a.choice.length > 0) ||
            (typeof a.choice === 'string' && a.choice)
        )
        .map(([questionId, a]) => ({
          questionId,
          valueText: a.text?.trim() || undefined,
          valueRating: a.rating,
          valueChoice: a.choice,
        }));
      await submitAnswers(survey.id, payload, payload.length === 0);
      setThanksOpen(true);
      setTimeout(() => setThanksOpen(false), 3500);
    } finally {
      setSending(false);
    }
  };

  const handleSkip = () => {
    if (isLast) {
      void handleNext();
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  const handleDismiss = async () => {
    await dismiss(survey.id);
  };

  return (
    // Pas de data-critical-modal-open ici : useSurveyDisplayGuard lit cet
    // attribut, le sondage se masquerait lui-meme en boucle (clignotement).
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden border border-brand-border animate-[fadeIn_220ms_ease-out]">
        {/* Header brand Glowp avec avatar fondateur */}
        <div className="px-6 pt-6 pb-5 bg-brand-offwhite border-b border-brand-border flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-500 to-brand-warm flex items-center justify-center text-white shadow-sm">
              {user?.firstName ? (
                <span className="text-base font-semibold">MA</span>
              ) : (
                <Heart className="w-5 h-5" />
              )}
            </div>
            <div>
              <h2 className="text-base font-semibold text-stone-900 leading-tight">
                {COPY.modalTitle}
              </h2>
              <p className="text-xs text-stone-500 mt-0.5">
                {COPY.modalSubtitle}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            aria-label={COPY.ctaDismiss}
            title={COPY.ctaDismiss}
            className="p-1.5 rounded-full hover:bg-white/60 text-stone-500 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Intro / progress */}
        {survey.intro && stepIndex === 0 && (
          <div className="px-6 pt-5 pb-2">
            <p className="text-sm text-stone-700 leading-relaxed">
              {survey.intro}
            </p>
          </div>
        )}

        {/* Question */}
        <div className="px-6 py-6">
          <p className="text-[15px] font-medium text-stone-900 mb-5 leading-snug">
            {question.label}
            {question.required && <span className="text-rose-500 ml-1">*</span>}
          </p>

          <QuestionInput
            question={question}
            current={current}
            onChange={setAnswerPart}
          />
        </div>

        {/* Footer : skip + next/submit + progress */}
        <div className="px-6 pb-5 pt-2 flex items-center justify-between gap-3">
          <button
            onClick={handleSkip}
            disabled={sending}
            className="text-xs text-stone-500 hover:text-stone-700 transition disabled:opacity-50"
          >
            {COPY.ctaSkip}
          </button>

          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition ${
                  i === stepIndex
                    ? 'bg-brand-500 w-4'
                    : i < stepIndex
                      ? 'bg-brand-warm'
                      : 'bg-stone-200'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={sending || (question.required && !hasAnswer)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-brand-500 to-brand-warm text-white text-sm font-medium shadow-sm hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLast ? (
              <>
                <Send className="w-4 h-4" />
                <span>{COPY.ctaSubmit}</span>
              </>
            ) : (
              <>
                <span>{COPY.ctaNext}</span>
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        {thanksOpen && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center text-center px-8">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-500 to-brand-warm flex items-center justify-center text-white mb-4">
              <Heart className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">
              {COPY.thankYouTitle}
            </h3>
            <p className="text-sm text-stone-600 max-w-sm leading-relaxed">
              {COPY.thankYouBody}
            </p>
            <p className="text-xs text-stone-500 mt-4 italic">
              {COPY.signatureFounder}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

interface QuestionInputProps {
  question: PendingQuestion;
  current: AnswerState;
  onChange: (part: AnswerState) => void;
}

const QuestionInput: React.FC<QuestionInputProps> = ({
  question,
  current,
  onChange,
}) => {
  if (question.type === 'TEXT') {
    return (
      <textarea
        value={current.text || ''}
        onChange={e => onChange({ text: e.target.value })}
        placeholder={COMMUNITY_VOICE_COPY_PLACEHOLDER}
        rows={4}
        maxLength={2000}
        className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 transition resize-none"
      />
    );
  }

  if (question.type === 'NPS') {
    const value = current.rating;
    return (
      <div>
        <div className="grid grid-cols-11 gap-1.5 mb-3">
          {Array.from({ length: 11 }).map((_, n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange({ rating: n })}
              className={`h-10 rounded-lg text-sm font-medium transition ${
                value === n
                  ? 'bg-brand-500 text-white shadow-sm'
                  : 'bg-stone-50 text-stone-700 hover:bg-brand-cream'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-[11px] text-stone-500">
          <span>Pas du tout</span>
          <span>Vraiment beaucoup</span>
        </div>
      </div>
    );
  }

  if (question.type === 'RATING') {
    const value = current.rating || 0;
    return (
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => onChange({ rating: n })}
            className={`p-2 rounded-full transition ${
              n <= value
                ? 'text-amber-500'
                : 'text-stone-300 hover:text-amber-400'
            }`}
            aria-label={`${n} étoiles`}
          >
            <Star
              className="w-7 h-7"
              fill={n <= value ? 'currentColor' : 'none'}
            />
          </button>
        ))}
      </div>
    );
  }

  if (question.type === 'MOOD' || question.type === 'SINGLE_CHOICE') {
    const options = question.options || [];
    const value = current.choice as string | undefined;
    return (
      <div className="grid grid-cols-1 gap-2">
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ choice: opt.value })}
            className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-sm text-left transition ${
              value === opt.value
                ? 'border-brand-500 bg-brand-cream text-stone-900 shadow-sm'
                : 'border-brand-border bg-white text-stone-700 hover:border-brand-warm/50 hover:bg-brand-offwhite'
            }`}
          >
            {renderIcon(
              opt.icon,
              `w-5 h-5 ${value === opt.value ? 'text-brand-500' : 'text-stone-400'}`
            )}
            <span className="flex-1">{opt.label}</span>
          </button>
        ))}
      </div>
    );
  }

  if (question.type === 'MULTI_CHOICE') {
    const options = question.options || [];
    const value: string[] = Array.isArray(current.choice) ? current.choice : [];
    const toggle = (v: string) => {
      const set = new Set(value);
      if (set.has(v)) set.delete(v);
      else set.add(v);
      onChange({ choice: Array.from(set) });
    };
    return (
      <div className="grid grid-cols-1 gap-2">
        {options.map(opt => {
          const checked = value.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-sm text-left transition ${
                checked
                  ? 'border-brand-500 bg-brand-cream text-stone-900 shadow-sm'
                  : 'border-brand-border bg-white text-stone-700 hover:border-brand-warm/50 hover:bg-brand-offwhite'
              }`}
            >
              <span
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${
                  checked
                    ? 'border-brand-500 bg-brand-500 text-white'
                    : 'border-stone-300'
                }`}
              >
                {checked && (
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-3 h-3"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 011.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className="flex-1">{opt.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return null;
};

const COMMUNITY_VOICE_COPY_PLACEHOLDER = 'Ta réponse, libre…';

export default CommunityVoiceModal;
